import { useCallback, useEffect, useRef, useState } from 'react';
import { usefulDocsAPI } from '@/lib/api';
import { publishUsefulDocsChange } from '@/lib/usefulDocumentEvents';

export function useObservedUsefulDocuments({ userId, documents, renderKey, onSeen }) {
    const queue = useRef(new Map());
    const acknowledged = useRef(new Set());
    const sending = useRef(false);
    const batchTimer = useRef(null);
    const mounted = useRef(false);
    const latest = useRef({ userId, onSeen });
    latest.current = { userId, onSeen };
    const [error, setError] = useState(false);

    const flush = useCallback(async () => {
        if (sending.current || !queue.current.size) return;
        const owner = latest.current.userId;
        const batch = [...queue.current.values()].slice(0, 500);
        batch.forEach(doc => queue.current.delete(doc.id));
        sending.current = true;
        let succeeded = false;
        try {
            const { data } = await usefulDocsAPI.markSeen({ documents: batch });
            if (mounted.current && owner === latest.current.userId) {
                batch.forEach(doc => acknowledged.current.add(`${doc.id}:${doc.revision}`));
                latest.current.onSeen(data.versions);
                setError(false);
            }
            publishUsefulDocsChange(owner, data.count);
            succeeded = true;
        } catch {
            if (!mounted.current || owner !== latest.current.userId) return;
            batch.forEach(doc => { if (!queue.current.has(doc.id)) queue.current.set(doc.id, doc); });
            setError(true);
        } finally {
            sending.current = false;
            if (succeeded && queue.current.size) batchTimer.current = setTimeout(flush, 200);
        }
    }, []);

    const markSeen = useCallback((doc) => {
        if (!doc?.id || !doc.revision || doc.is_unread === false || acknowledged.current.has(`${doc.id}:${doc.revision}`)) return;
        queue.current.set(doc.id, { id: doc.id, revision: doc.revision });
        clearTimeout(batchTimer.current);
        batchTimer.current = setTimeout(flush, 200);
    }, [flush]);

    useEffect(() => {
        mounted.current = true;
        queue.current.clear(); acknowledged.current.clear(); setError(false);
        window.addEventListener('focus', flush);
        window.addEventListener('online', flush);
        return () => {
            // Transmettre les vues déjà observées avant de quitter, pas les cartes cachées.
            clearTimeout(batchTimer.current);
            if (userId === latest.current.userId) flush(); else queue.current.clear();
            mounted.current = false;
            window.removeEventListener('focus', flush); window.removeEventListener('online', flush);
        };
    }, [userId, flush]);

    useEffect(() => {
        if (!userId || !window.IntersectionObserver) return;
        const byId = new Map(documents.map(doc => [doc.id, doc]));
        const visible = new Set(), timers = new Map();
        const start = (element) => {
            const doc = byId.get(element.dataset.usefulDocumentId);
            if (document.hidden || !doc?.is_unread || timers.has(element)) return;
            timers.set(element, setTimeout(() => {
                timers.delete(element);
                if (!document.hidden && visible.has(element)) markSeen(doc);
            }, 700));
        };
        const observer = new IntersectionObserver(entries => entries.forEach(entry => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) { visible.add(entry.target); start(entry.target); }
            else { visible.delete(entry.target); clearTimeout(timers.get(entry.target)); timers.delete(entry.target); }
        }), { threshold: [0, 0.5] });
        document.querySelectorAll('[data-useful-document-id]').forEach(element => observer.observe(element));
        const onVisibility = () => {
            timers.forEach(clearTimeout); timers.clear();
            if (!document.hidden) visible.forEach(start);
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => { observer.disconnect(); timers.forEach(clearTimeout); document.removeEventListener('visibilitychange', onVisibility); };
    }, [userId, documents, renderKey, markSeen]);

    return { markSeen, error, retry: flush };
}